import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { canAccessModule } from "@/utils/permissions";
import { toast } from "sonner";
import "./cabina.css";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import ClientCombobox from "@/components/ClientCombobox";

const fmt = (v) => (v == null ? "—" : `$${Math.round(Number(v)).toLocaleString("es-MX")}`);
const fmtDec = (v) => (v == null ? "—" : `$${Number(v).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

// Iconos SVG mismos del HTML
const I = {
  open: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18" /><path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" /><path d="M6 6l1 14a2 2 0 002 2h6a2 2 0 002-2l1-14" /></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4 12.5-12.5z" /></svg>,
  send: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4 20-7z" /></svg>,
  wallet: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12V8a2 2 0 00-2-2H5a2 2 0 000 4h15a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2V6" /></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>,
  dollar: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" /></svg>,
};

const etapaChip = (e, facturadoCompleto = true) => {
  const map = {
    por_validar_costos: ["c-pend", "Por validar"],
    listo_para_facturar: ["c-val", "Listo"],
    factura_solicitada: ["c-sol", "Solicitada"],
    facturado: [facturadoCompleto === false ? "c-warn" : "c-fact", facturadoCompleto === false ? "Fact. parcial" : "Facturado"],
    cancelado: ["c-canc", "Cancelado"],
  };
  const [cls, l] = map[e] || ["c-pend", e];
  return <span className={`chip ${cls}`}>{l}</span>;
};

export default function CabinaVentas() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("expedientes");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [canal, setCanal] = useState("");
  const [etapa, setEtapa] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [resumen, setResumen] = useState(null);
  const [mixCanal, setMixCanal] = useState({ rows: [] });
  const [mixGrupo, setMixGrupo] = useState({ rows: [] });
  const [showNuevo, setShowNuevo] = useState(false);
  const canVerMargen = canAccessModule(user, "cabina_ver_margenes");

  const loadRows = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/cabina/expedientes`, { params: { q, canal, etapa, desde, hasta, page_size: 200 } });
      setRows(r.data.rows || []);
    } catch (e) { toast.error("Error cargando expedientes"); }
  }, [q, canal, etapa, desde, hasta]);

  const loadTablero = useCallback(async () => {
    try {
      const [r1, r2, r3] = await Promise.all([
        axios.get(`${API}/cabina/tablero/resumen`),
        axios.get(`${API}/cabina/tablero/mix`, { params: { dimension: "canal" } }),
        axios.get(`${API}/cabina/tablero/mix`, { params: { dimension: "grupo" } }),
      ]);
      setResumen(r1.data); setMixCanal(r2.data); setMixGrupo(r3.data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { loadRows(); }, [loadRows]);
  useEffect(() => { loadTablero(); }, [loadTablero]);

  const eliminar = async (exp) => {
    const motivo = prompt(`Cancelar expediente ${exp.folio}. Motivo:`);
    if (!motivo) return;
    try { await axios.delete(`${API}/cabina/expedientes/${exp.id}`, { params: { motivo } }); toast.success("Expediente cancelado"); loadRows(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const solicitar = async (exp) => {
    const nota = prompt(`Solicitar factura para ${exp.folio}\nNota para administración (opcional):`) || "";
    try { await axios.post(`${API}/cabina/expedientes/${exp.id}/solicitar-factura`, { nota }); toast.success("Factura solicitada"); loadRows(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="cv" data-testid="cabina-ventas">
      <div className="head">
        <div>
          <h2>Cabina de Ventas</h2>
          <p>Expedientes de venta, costos y tablero de desempeño.</p>
        </div>
        <div className="spacer" />
        <button className="btn" onClick={() => setShowNuevo(true)} data-testid="btn-nuevo-expediente">
          <span className="mono" style={{ fontWeight: 900 }}>+</span> Nuevo expediente
        </button>
      </div>

      <div className="cv-subtabs">
        <button className={tab === "expedientes" ? "on" : ""} onClick={() => setTab("expedientes")} data-testid="tab-expedientes">
          Expedientes <span className="badge">{rows.length}</span>
        </button>
        <button className={tab === "tablero" ? "on" : ""} onClick={() => setTab("tablero")} data-testid="tab-tablero">
          Tablero de Ventas
        </button>
      </div>

      {tab === "expedientes" && (
        <>
          <div className="filtros">
            <label className="f"><span>Buscar</span><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Folio, cliente, pasajero, proyecto" data-testid="search-expedientes" /></label>
            <label className="f"><span>Canal</span>
              <select value={canal} onChange={(e) => setCanal(e.target.value)} data-testid="filter-canal">
                <option value="">Todos los canales</option>
                <option value="business">Business Travel</option>
                <option value="groups">Grupos &amp; Eventos / FIT</option>
                <option value="directo">Directo</option>
                <option value="socio">Socio</option>
              </select>
            </label>
            <label className="f"><span>Estatus</span>
              <select value={etapa} onChange={(e) => setEtapa(e.target.value)} data-testid="filter-etapa">
                <option value="">Todos (activos)</option>
                <option value="por_validar_costos">Por validar costos</option>
                <option value="listo_para_facturar">Listo para facturar</option>
                <option value="factura_solicitada">Factura solicitada</option>
                <option value="facturado">Facturado</option>
                <option value="cancelado">Cancelados</option>
              </select>
            </label>
            <label className="f"><span>Venta desde</span><input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} /></label>
            <label className="f"><span>Venta hasta</span><input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} /></label>
            <button className="btn ghost sm" style={{ alignSelf: "end", height: 36 }} onClick={() => { setQ(""); setCanal(""); setEtapa(""); setDesde(""); setHasta(""); }}>Limpiar</button>
            <div className="fcount">Mostrando <b>{rows.length}</b> expedientes</div>
          </div>

          <div className="card">
            <div className="tw">
              <table className="dense">
                <thead><tr>
                  <th style={{ lineHeight: 1.25 }}>Fecha<br />venta</th>
                  <th className="nw">Registro</th>
                  <th className="nw">Folio</th>
                  <th>Cliente</th>
                  <th>Canal</th>
                  <th className="r">PAX</th>
                  <th className="nw">Servicios</th>
                  <th className="r nw">Subtotal</th>
                  <th className="r nw">IVA</th>
                  <th className="r nw">Total</th>
                  {canVerMargen && <th className="r nw">Utilidad</th>}
                  <th className="nw">Estatus</th>
                  <th className="r nw" style={{ width: "1%" }} />
                </tr></thead>
                <tbody>
                  {rows.length === 0 && <tr><td colSpan={canVerMargen ? 13 : 12} className="empty"><b>Sin expedientes</b>Crea uno con el botón &quot;Nuevo expediente&quot;.</td></tr>}
                  {rows.map((r) => {
                    const t = r.totales || {};
                    const facturado = r.etapa === "facturado";
                    return (
                      <tr key={r.id} className="click" onClick={() => nav(`/cabina-ventas/expediente/${r.id}`)} data-testid={`row-exp-${r.folio}`}>
                        <td className="nw mono">{r.fecha_venta}</td>
                        <td className="nw mono" style={{ color: "var(--cv-ghost)" }}>{(r.registro || "").slice(0, 10)}</td>
                        <td className="nw"><b style={{ color: "var(--cv-blue)", fontFamily: "'IBM Plex Mono',monospace" }}>{r.folio}</b></td>
                        <td>{r.cliente_nombre || "—"}<div className="s" title={
                          (r.canal === "groups" || r.subtipo === "fit") ? (r.proyecto_nombre || "") : ((r.pasajeros || []).map(p => p.nombre).filter(Boolean).join(", "))
                        }>{(() => {
                          if ((r.canal === "groups" || r.subtipo === "fit") && r.proyecto_nombre) return r.proyecto_nombre;
                          const nombres = (r.pasajeros || []).map(p => p.nombre).filter(Boolean);
                          if (nombres.length === 0) return r.cliente_rfc || "";
                          if (nombres.length <= 2) return nombres.join(", ");
                          return `${nombres.slice(0, 2).join(", ")} + ${nombres.length - 2} más`;
                        })()}</div></td>
                        <td><span className="chip c-canal">{r.canal}</span></td>
                        <td className="r mono">{r.pax || 0}</td>
                        <td className="nw">{t.servicios_totales || 0}<div className="s">{t.servicios_validados || 0} validado(s)</div></td>
                        <td className="r nw mono">{fmt(facturado ? t.subtotal_facturado : t.subtotal_estimado)}<span className={`bdg ${facturado ? "firme" : "est"}`}>{facturado ? "firme" : "est"}</span></td>
                        <td className="r nw mono">{fmt(facturado ? t.iva_facturado : t.iva_estimado)}</td>
                        <td className="r nw mono"><b>{fmt(facturado ? t.facturado : (t.subtotal_estimado || 0) + (t.iva_estimado || 0))}</b></td>
                        {canVerMargen && <td className="r nw mono" style={{ color: "var(--cv-blue)" }}>{fmt(facturado ? t.utilidad_real : t.utilidad_estimada)}</td>}
                        <td>{etapaChip(r.etapa, (r.totales || {}).facturado_completo !== false)}<div className="s" style={{ margin: 0 }}>{(r.totales?.servicios_validados || 0)}/{(r.totales?.servicios_totales || 0)} costos</div></td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="ibs">
                            <button className="ib" title="Abrir" onClick={() => nav(`/cabina-ventas/expediente/${r.id}`)} data-testid={`ib-open-${r.folio}`}>{I.open}</button>
                            {(r.etapa === "por_validar_costos" || r.etapa === "listo_para_facturar") && <button className="ib" title="Solicitar factura" onClick={() => solicitar(r)} data-testid={`ib-sol-${r.folio}`}>{I.send}</button>}
                            {(r.etapa === "listo_para_facturar" || r.etapa === "factura_solicitada") && <button className="ib pri" title="Ir a registrar factura" onClick={() => nav(`/cabina-ventas/expediente/${r.id}`)} data-testid={`ib-fac-${r.folio}`}>{I.wallet}</button>}
                            {!facturado && <button className="ib danger" title="Cancelar" onClick={() => eliminar(r)} data-testid={`ib-del-${r.folio}`}>{I.trash}</button>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === "tablero" && (
        <>
          <div className="card">
            <div className="card-h"><h3>Dinero</h3><span className="sub">Valores según etapa del ciclo</span></div>
            <div className="card-b">
              <div className="kpis">
                <Kpi k="Venta bruta" v={fmt(resumen?.venta_bruta)} s={`${resumen?.expedientes || 0} expedientes`} />
                <Kpi k="Subtotal estimado" v={fmt(resumen?.subtotal_estimado)} />
                <Kpi k="IVA estimado" v={fmt(resumen?.iva_estimado)} />
                <Kpi k="Facturado" v={fmt(resumen?.facturado)} s={`Por facturar: ${fmt(resumen?.por_facturar)}`} />
                <Kpi k="Cobrado" v={fmt(resumen?.cobrado)} s={`Por cobrar: ${fmt(resumen?.por_cobrar)}`} />
                {canVerMargen && <Kpi k="Utilidad confirmada" v={fmt(resumen?.utilidad_confirmada)} s="costos validados" />}
                {canVerMargen && <Kpi k="Utilidad estimada" v={fmt(resumen?.utilidad_estimada)} s="incluye supuestos" mut />}
                {canVerMargen && <Kpi k="Utilidad real" v={resumen?.utilidad_real_disponible ? fmt(resumen?.utilidad_real) : "—"} s={`${resumen?.utilidad_real_disponible || 0} expedientes facturados`} dark />}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-h"><h3>Etapas de facturación</h3><span className="sub">dónde está atorado el ciclo</span></div>
            <div className="card-b">
              <div className="kpis">
                <Kpi k="Por validar costos" v={String((resumen?.etapas || {}).por_validar_costos || 0)} dark />
                <Kpi k="Listo para facturar" v={String((resumen?.etapas || {}).listo_para_facturar || 0)} />
                <Kpi k="Factura solicitada" v={String((resumen?.etapas || {}).factura_solicitada || 0)} />
                <Kpi k="Facturado" v={String((resumen?.etapas || {}).facturado || 0)} />
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-h"><h3>Volumen</h3><span className="sub">PAX y servicios movidos en el periodo</span></div>
            <div className="card-b">
              <div className="kpis">
                <Kpi k="Expedientes" v={String(resumen?.expedientes || 0)} dark />
                <Kpi k="PAX movidos" v={String(resumen?.pax_movidos || 0)} />
                <Kpi k="Servicios" v={String(resumen?.servicios || 0)} />
              </div>
            </div>
          </div>

          <div className="split" style={{ marginTop: 16 }}>
            <div className="card">
              <div className="card-h"><h3>Mix por canal</h3><span className="sub">estimado</span></div>
              <div className="tw">
                <table>
                  <thead><tr><th>Canal</th><th className="r">Venta</th>{canVerMargen && <th className="r">Utilidad</th>}<th className="r">Servicios</th></tr></thead>
                  <tbody>
                    {(mixCanal.rows || []).length === 0 && <tr><td colSpan={canVerMargen ? 4 : 3} className="empty">Sin datos</td></tr>}
                    {(mixCanal.rows || []).map((r) => (
                      <tr key={r.nombre}>
                        <td><span className="chip c-canal">{r.nombre}</span></td>
                        <td className="r mono">{fmt(r.venta)}</td>
                        {canVerMargen && <td className="r mono" style={{ color: "var(--cv-blue)" }}>{fmt(r.utilidad)}</td>}
                        <td className="r mono">{r.servicios}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="card">
              <div className="card-h"><h3>Mix por grupo</h3><span className="sub">de servicio</span></div>
              <div className="tw">
                <table>
                  <thead><tr><th>Grupo</th><th className="r">Venta</th>{canVerMargen && <th className="r">Utilidad</th>}<th className="r">Servicios</th></tr></thead>
                  <tbody>
                    {(mixGrupo.rows || []).length === 0 && <tr><td colSpan={canVerMargen ? 4 : 3} className="empty">Sin datos</td></tr>}
                    {(mixGrupo.rows || []).map((r) => (
                      <tr key={r.nombre}>
                        <td>{r.nombre}</td>
                        <td className="r mono">{fmt(r.venta)}</td>
                        {canVerMargen && <td className="r mono" style={{ color: "var(--cv-blue)" }}>{fmt(r.utilidad)}</td>}
                        <td className="r mono">{r.servicios}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      {showNuevo && <NuevoExpedienteDialog onClose={() => setShowNuevo(false)} onCreated={(id) => { setShowNuevo(false); nav(`/cabina-ventas/expediente/${id}`); }} />}
    </div>
  );
}

const Kpi = ({ k, v, s, dark, mut }) => (
  <div className="kpi">
    <div className="k">{k}</div>
    <div className={`v ${dark ? "dark" : ""} ${mut ? "mut" : ""}`}>{v}</div>
    {s && <div className="s">{s}</div>}
  </div>
);

// -------- Nuevo Expediente (usa ClientCombobox del backoffice) --------
function NuevoExpedienteDialog({ onClose, onCreated }) {
  const { user } = useAuth();
  const [cat, setCat] = useState(null);
  const [empleados, setEmpleados] = useState([]);
  const [cliente, setCliente] = useState(null);
  const [pasajerosTxt, setPasajerosTxt] = useState("");
  // Estrategia comercial de márgenes mínimos por canal/subtipo
  const getMargenDefault = (canal, subtipo) => {
    if (canal === "business") return 15;
    if (canal === "groups") return 20;
    if (canal === "directo") return subtipo === "friends_family" ? 10 : 15;
    if (canal === "socio") return 20;
    return 15;
  };
  const [form, setForm] = useState({
    canal: "business", subtipo: "",
    agente_id: "",
    agente_nombre: "",
    proyecto_nombre: "",
    pax: 50,
    fecha_venta: new Date().toISOString().slice(0, 10),
    margen_minimo_pct: 15, comision_socio_pct: 5,
    notas: "",
  });
  useEffect(() => {
    axios.get(`${API}/cabina/catalogo`).then((r) => setCat(r.data));
    axios.get(`${API}/employees`).then((r) => {
      setEmpleados(r.data || []);
      // Preseleccionar empleado enlazado al usuario logueado
      const linked = (r.data || []).find((e) => e.user_id === user?.id);
      if (linked) setForm((f) => ({ ...f, agente_id: linked.id, agente_nombre: linked.nombre }));
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Subtipo aplica para "groups" (Grupo/Evento/FIT) y "directo" (Friends & Family / Normal).
  // Los demás canales no tienen subtipo (se envía vacío al backend).
  // Al cambiar canal/subtipo se ajusta automáticamente el margen mínimo según la estrategia comercial.
  useEffect(() => {
    setForm((f) => {
      let subtipo = f.subtipo;
      if (f.canal === "groups") {
        if (!["grupo", "evento", "fit"].includes(subtipo)) subtipo = "grupo";
      } else if (f.canal === "directo") {
        if (!["normal", "friends_family"].includes(subtipo)) subtipo = "normal";
      } else if (subtipo) {
        subtipo = "";
      }
      const margen = getMargenDefault(f.canal, subtipo);
      return { ...f, subtipo, margen_minimo_pct: margen };
    });
  }, [form.canal, form.subtipo && form.subtipo]); // eslint-disable-line react-hooks/exhaustive-deps

  const esGroups = form.canal === "groups";
  const esSocio = form.canal === "socio";
  const esDirecto = form.canal === "directo";

  const submit = async () => {
    if (!cliente) { toast.error("Selecciona un cliente"); return; }
    if (!esGroups && !pasajerosTxt.trim()) { toast.error("Captura al menos un pasajero"); return; }
    if (esGroups && !form.proyecto_nombre.trim()) { toast.error("Ingresa el nombre del proyecto"); return; }
    const pasajeros = esGroups ? [] : pasajerosTxt.split("\n").map((n) => n.trim()).filter(Boolean).map((nombre) => ({ nombre }));
    try {
      const payload = {
        ...form,
        cliente_id: cliente.id, cliente_nombre: cliente.company_name, cliente_rfc: cliente.rfc || "",
        pasajeros, pax: esGroups ? Number(form.pax) : pasajeros.length,
      };
      const r = await axios.post(`${API}/cabina/expedientes`, payload);
      toast.success(`Expediente ${r.data.expediente.folio} creado`);
      onCreated(r.data.expediente.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="cv" style={{ maxWidth: 720, padding: 0, maxHeight: "90vh", overflow: "auto" }}>
        <div style={{ padding: "16px 22px", borderBottom: "1px solid var(--cv-line)" }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Nuevo expediente</h3>
          <p style={{ margin: "2px 0 0", color: "var(--cv-muted)", fontSize: 12 }}>Ruta, fechas y medios se capturan al agregar cada servicio.</p>
        </div>
        <div style={{ padding: 22 }}>
          <div className="grid g2">
            <label className="f"><span>Canal</span>
              <select value={form.canal} onChange={(e) => setForm({ ...form, canal: e.target.value })} data-testid="nuevo-canal">
                {(cat?.canales || []).map((c) => <option key={c.codigo} value={c.codigo}>{c.nombre}</option>)}
              </select>
            </label>
            {esGroups ? (
              <label className="f"><span>Subtipo</span>
                <select value={form.subtipo} onChange={(e) => setForm({ ...form, subtipo: e.target.value })} data-testid="nuevo-subtipo">
                  <option value="grupo">Grupo</option>
                  <option value="evento">Evento</option>
                  <option value="fit">FIT</option>
                </select>
              </label>
            ) : esDirecto ? (
              <label className="f"><span>Subtipo</span>
                <select value={form.subtipo} onChange={(e) => setForm({ ...form, subtipo: e.target.value })} data-testid="nuevo-subtipo">
                  <option value="normal">Normal</option>
                  <option value="friends_family">Friends &amp; Family</option>
                </select>
              </label>
            ) : <div />}
            <label className="f" style={{ gridColumn: "1 / -1" }}>
              <span>Cliente</span>
              <ClientCombobox value={cliente?.company_name || ""} onSelect={setCliente} onClear={() => setCliente(null)} testid="nuevo-cliente" />
            </label>
            <label className="f" style={{ gridColumn: "1 / -1" }}><span>Agente</span>
              <select
                value={form.agente_id}
                onChange={(e) => {
                  const eid = e.target.value;
                  const emp = empleados.find((x) => x.id === eid);
                  setForm({ ...form, agente_id: eid, agente_nombre: emp?.nombre || "" });
                }}
                data-testid="nuevo-agente"
              >
                <option value="">— Selecciona un empleado —</option>
                {empleados.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.nombre}{e.puesto ? ` · ${e.puesto}` : ""}
                  </option>
                ))}
              </select>
              {empleados.length === 0 && <div className="hint" style={{ color: "var(--cv-night)" }}>Sin empleados cargados. Ve a Configuración → Empleados.</div>}
            </label>
            {!esGroups && (
              <label className="f" style={{ gridColumn: "1 / -1" }}><span>Pasajeros (uno por renglón)</span>
                <textarea rows={3} value={pasajerosTxt} onChange={(e) => setPasajerosTxt(e.target.value)} placeholder="Fernando Gomez" data-testid="nuevo-pasajeros" />
                <div className="hint">Se calcula PAX automáticamente. Para Grupos usa el conteo del proyecto.</div>
              </label>
            )}
            {esGroups && (
              <>
                <label className="f"><span>PAX confirmados del proyecto</span>
                  <input type="number" value={form.pax ?? ""} onChange={(e) => setForm({ ...form, pax: e.target.value === "" ? "" : Number(e.target.value) })} min={1} data-testid="nuevo-pax-grupo" />
                </label>
                <label className="f"><span>Nombre del proyecto</span>
                  <input value={form.proyecto_nombre} onChange={(e) => setForm({ ...form, proyecto_nombre: e.target.value })} placeholder="Convención Hisense ATL abril 26" data-testid="nuevo-proyecto" />
                </label>
              </>
            )}
            <label className="f"><span>Fecha de venta</span>
              <input type="date" value={form.fecha_venta} onChange={(e) => setForm({ ...form, fecha_venta: e.target.value })} />
            </label>
            <label className="f"><span>Margen mínimo de la venta %</span>
              <input type="number" value={form.margen_minimo_pct ?? ""} onChange={(e) => setForm({ ...form, margen_minimo_pct: e.target.value === "" ? "" : Number(e.target.value) })} data-testid="nuevo-margen-min" />
              <div className="hint">Sugerido por estrategia: Business 15% · Grupos 20% · Directo Normal 15% · Directo F&amp;F 10% · Socios 20%</div>
            </label>
            {esSocio && (
              <label className="f"><span>Comisión socio %</span>
                <input type="number" value={form.comision_socio_pct ?? ""} onChange={(e) => setForm({ ...form, comision_socio_pct: e.target.value === "" ? "" : Number(e.target.value) })} />
              </label>
            )}
            <label className="f" style={{ gridColumn: "1 / -1" }}><span>Notas</span>
              <textarea rows={2} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} />
            </label>
          </div>
        </div>
        <div className="dlg-foot">
          <button className="btn ghost sm" onClick={onClose}>Cancelar</button>
          <button className="btn sm" onClick={submit} data-testid="btn-crear-expediente">Crear expediente</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
